<template>
  <div style="height:100%; width: 100%; display: flex; flex-direction: column; justify-content: flex-start;">
    <alert-cloning-panel
      :showModal="showModal"
      @close="showModal = false"
      :availableCameras="Object.entries(camerasById)"
      @clone="cloneAlert"
      :initialDescription="`Alert ${Object.keys(this.alertsById).length + 1}`"
      />
    <div >
      <div class="button-container" style="top:12rem;">
        <owal-button @click="addAlert()" tooltip="create alert" class="add-alert-button">
          <font-awesome-layers slot="accessory" class="fa-lg" >
            <font-awesome-icon icon="bell" style="color: rgba(168, 168, 168, 1)" />
            <font-awesome-icon icon="plus" transform="shrink-6 left-6 down-4" style="color: rgba(68, 68, 68, .75)"/>
          </font-awesome-layers>
        </owal-button>
      </div>
      <div v-if="currentAlertId" class="button-container" style="top:15rem;" >
        <owal-button @click="addCondition()" tooltip="add condition" class="add-detectable-button">
          <font-awesome-layers slot="accessory" class="fa-lg">
            <font-awesome-icon icon="dna" style="color: rgba(168, 168, 168, 1)"/>
            <font-awesome-icon icon="plus" transform="shrink-6 left-6 down-4" style="color: rgba(68, 68, 68, .75)"/>
          </font-awesome-layers>
        </owal-button>
      </div>
      <div v-if="currentAlertId" class="button-container" style="top:18rem;">
        <owal-button @click="addNotification()" tooltip="add notification" class="add-notification-button">
          <font-awesome-layers slot="accessory" class="fa-lg">
            <font-awesome-icon icon="envelope" style="color: rgba(168, 168, 168, 1)"/>
            <font-awesome-icon icon="plus" transform="shrink-6 left-6 down-4" style="color: rgba(68, 68, 68, .75)"/>
          </font-awesome-layers>
        </owal-button>
      </div>
      <div v-if="currentAlertId" class="button-container" style="top:21rem;">
        <owal-button @click="showModal = true" tooltip="clone alert" class="clone-alert-button">
          <font-awesome-icon class="fa-lg" icon="clone" style="color: rgba(168, 168, 168, 1)"/>
        </owal-button>
      </div>
    </div>
    <config-form
      :formGroups="formGroups"
      @change="passChanges"
      @deactivate="handleDeactivate"
      />
  </div>
</template>

<script>
import ConfigForm from './ConfigForm'
import OwalButton from '@/libraries/owal-ui/buttons/OwalButton'
import { mapState, mapGetters, mapMutations } from 'vuex'
import { groupsFromNotificationConfig, groupsFromConditionConfig } from './dnaFormHelper'
import AlertCloningPanel from './AlertCloningPanel'
import { Operation, Service } from '@/libraries/permissions'

export default {
  name: 'DNAConfigAlertsView',
  components: {
    ConfigForm,
    OwalButton,
    AlertCloningPanel,
  },
  props: {
  },
  data () {
    return {
      editing: false,
      showModal: false,
    }
  },
  methods: {
    ...mapMutations({
      setCurrentAlertId: 'dnaConfiguration/setCurrentAlertId',
    }),
    passChanges (change) {
      const current = { ...this.alertsById[this.currentAlertId] }
      if (change.group !== 'alerts') {
        const alert = {
          group: 'alerts',
          parentId: ['alert', this.currentAlertId],
          values: { ...current },
        }
        this.$emit('change', alert)
        this.$emit('change', change)
      } else {
        const alert = {
          group: 'alerts',
          parentId: ['alert', this.currentAlertId],
          values: { ...current, ...change.values },
        }
        this.$emit('change', alert)
      }
    },
    addNotification (values) {
      const newId = `NEW-${Object.keys(this.notificationsById).length + 1}`
      const current = { ...this.alertsById[this.currentAlertId] }
      const alert = {
        group: 'alerts',
        parentId: ['alert', this.currentAlertId],
        values: { ...current, notifications: [ newId, ...current.notifications ] },
      }
      const notification = {
        group: 'notifications',
        parentId: ['notification', newId],
        values: values || {
          activeDays: [],
          endOffset: 0,
          endOffsetFrom: 'midnight',
          startOffset: 0,
          startOffsetFrom: 'midnight',
          schedule_id: null,
          type: 'slack',
          // TODO(Yeedle): handle case where domain doesn't have channel yet
          info: this.notificationTargets.filter(t => t.notification_target_type_name === 'slack')[0].notification_target_info,
          target_id: null,
        },
      }

      this.$emit('change', notification)
      this.$emit('change', alert)
    },
    addCondition (values) {
      const newId = `NEW-${Object.keys(this.conditionsById).length + 1}`
      const condition = {
        group: 'conditions',
        parentId: ['condition', newId],
        values: values || {
          confidence: 0.0,
          min: 1,
          max: 2147483647,
          debounce: 5,
          detectableDescription: this.detectables[0].class_name,
          detectableId: this.detectables[0].id,
          id: this.detectables[0].id,
          duration: 0,
          expiration: 0,
        },
      }
      const current = { ...this.alertsById[this.currentAlertId] }
      const alert = {
        group: 'alerts',
        parentId: ['alert', this.currentAlertId],
        values: { ...current, conditions: [ newId, ...current.conditions ] },
      }
      this.$emit('change', condition)
      this.$emit('change', alert)
    },
    cloneAlert (targetSettings) {
      const currentAlert = this.alertsById[this.currentAlertId]
      this.addAlert(targetSettings.description, targetSettings.cameras)
      currentAlert.notifications.forEach(notificationId => {
        this.addNotification(this.notificationsById[notificationId])
      })
      currentAlert.conditions.forEach(conditionId => {
        this.addCondition(this.conditionsById[conditionId])
      })
    },
    addAlert (description, cameras) {
      const newId = `NEW-${Object.keys(this.alertsById).length + 1}`
      const alert = {
        group: 'alerts',
        parentId: ['alert', newId],
        values: {
          description: description || 'New Alert',
          conditions: [],
          notifications: [],
          cameras: cameras || [],
        },
      }
      this.setCurrentAlertId(newId)
      this.$emit('change', alert)
    },
    handleDeactivate (change) {
      const alert = Object.assign(this.alertsById[this.currentAlertId])
      const alertChange = {
        group: 'alerts',
        parentId: ['alert', this.currentAlertId],
        values: { ...alert },
      }
      switch (change.parentId[0]) {
        case ('notification'):
          alertChange.values.notifications = alertChange.values.notifications.filter(id => id !== change.parentId[1])
          this.$emit('change', alertChange)
          break
        case ('condition'):
          alertChange.values.conditions = alertChange.values.conditions.filter(id => id !== change.parentId[1])
          this.$emit('change', alertChange)
          break
        case ('alert'):
          // Additional change event to remove alert
          this.$emit('change', { ...change, group: 'deactivate-alert' })
          break
        default:
          break
      }
      this.$emit('change', change)
    },
  },
  computed: {
    ...mapState('dnaConfiguration', {
      camerasById: state => state.camerasById,
      alertsById: state => state.alertsById,
      notificationsById: state => state.notificationsById,
      conditionsById: state => state.conditionsById,
      cameraDetectablesById: state => state.cameraDetectablesById,
      detectables: state => state.detectables,
      currentAlertId: state => state.currentAlertId,
      notificationTargets: state => state.notificationTargets,
    }),
    ...mapGetters({
      displayName: 'displayName', // this is actually the email,
      canPerformOperationForService: 'entitlements/canPerformOperationForService',
    }),
    hasOnlyLimitedEditing () {
      return this.canPerformOperationForService(Service.ALERTING, Operation.LIMITED_EDIT) && !this.canPerformOperationForService(Service.ALERTING, Operation.EDIT)
    },
    hasFullEditing() {
      return this.canPerformOperationForService(Service.ALERTING, Operation.EDIT)
    },
    availableCameras () {
      return Object.entries(this.camerasById).map(camera => camera[1].description)
    },
    formGroups () {
      if (this.currentAlertId && this.alertsById[this.currentAlertId]) {
        const alertId = this.currentAlertId
        const alert = { ...this.alertsById[this.currentAlertId] }
        const cameras = alert.cameras.map(cameraId => {
          return this.camerasById[cameraId].description
        })
        const alertNotifications = alert.notifications.reduce((prev, notificationId) => {
          prev[notificationId] = this.notificationsById[notificationId]
          return prev
        }, {})
        const notifications = groupsFromNotificationConfig(alertNotifications)
        const alertConditions = alert.conditions.reduce((prev, conditionId) => {
          prev[conditionId] = { ...this.conditionsById[conditionId] }
          return prev
        }, {})
        // note: detectable Ids are not kept up to date but class_name is
        const cameraDetectableDescriptions = alert.cameras.reduce(function (prev, cameraId) {
          this.camerasById[cameraId].detectables.forEach(function (cameraDetectableId) {
            prev.add(this[cameraDetectableId].detectable)
          }.bind(this.cameraDetectablesById))
          return prev
        }.bind(this), new Set([]))
        const conditions = groupsFromConditionConfig(alertConditions, this.detectables.filter(detectable => Array.from(cameraDetectableDescriptions).includes(detectable.class_name)))
        const activeCameras = {
          label: 'Active Cameras',
          type: 'select',
          multiple: true,
          value: cameras,
          identifiers: { alert: alertId, key: 'cameras' },
          options: this.availableCameras,
        }
        const description = {
          label: 'Description',
          value: alert.description,
          type: 'text',
          identifiers: { alert: alertId, key: 'description' },
        }
        const alertGroup = {
          title: 'Alert Info',
          editable: true,
          initialValues: alert,
          identifiers: { alert: alertId, key: 'alerts' },
          fields: [ description, activeCameras ],
        }
        return [alertGroup, ...conditions, ...notifications]
      } else return []
    },
  },
  mounted () {
  },
}
</script>

<style>
.button-container {
  position:absolute;
  z-index:100;
  right:10%;
  display: flex;
  flex-direction: column;
  padding-bottom: 1rem;
}
</style>
